import { AnvilIcon } from "@/components/icons/AnvilIcon";
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { ArrowUpRight, Loader2 } from "lucide-react";

const STAGE_LABEL = {
  "2": "Forms", "3": "Capture + Outline", "4": "Pricing", "5": "Draft",
  "6": "Compliance", "7": "Submit prep", "7B": "Bonding",
};

/* Dir 70 payoff — "runs in progress" strip. Polls /bids/active-runs so any teammate can SEE and JUMP INTO
   whatever Anvil is drafting across the team, live. Operator-only cockpit signal; hidden when nothing runs. */
export function LiveRuns() {
  const [runs, setRuns] = useState([]);
  const navigate = useNavigate();
  const timer = useRef(null);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const { data } = await api.get("/bids/active-runs");
        if (alive) setRuns(data.runs || []);
      } catch { /* transient */ }
      if (alive) timer.current = setTimeout(load, 8000);
    };
    load();
    return () => { alive = false; if (timer.current) clearTimeout(timer.current); };
  }, []);

  if (!runs.length) return null;

  return (
    <div className="mb-6 rounded-lg border border-amber-200 bg-amber-50/60 p-4" data-testid="live-runs-strip">
      <div className="flex items-center gap-2 mb-3">
        <span className="relative flex h-2.5 w-2.5">
          <span className="absolute inline-flex h-full w-full rounded-full bg-amber-500 opacity-70 animate-ping" />
          <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-amber-600" />
        </span>
        <AnvilIcon className="h-4 w-4 text-amber-700" />
        <span className="text-sm font-semibold text-zinc-900" data-testid="live-runs-count">
          Anvil is drafting now · {runs.length}
        </span>
        <span className="text-[11px] text-zinc-500">runs survive navigation — jump in anytime</span>
      </div>
      <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
        {runs.map((r, i) => (
          <button
            key={r.id}
            data-testid={`live-run-${i}`}
            onClick={() => navigate(`/deals/${r.id}`)}
            className="group flex items-center justify-between gap-3 rounded-md border border-amber-200 bg-white px-3 py-2 text-left hover:border-amber-400 hover:shadow-sm transition-colors"
          >
            <div className="min-w-0">
              <div className="text-sm text-zinc-900 truncate">{r.title}</div>
              <div className="text-[11px] text-zinc-500 font-mono truncate flex items-center gap-1.5">
                <Loader2 className="h-3 w-3 animate-spin text-amber-600 shrink-0" />
                {r.current_stage
                  ? `Stage ${r.current_stage} · ${STAGE_LABEL[r.current_stage] || r.current_stage_name || "running"}`
                  : "starting…"}
                {r.code ? ` · ${r.code}` : ""}
              </div>
            </div>
            <ArrowUpRight className="h-4 w-4 text-zinc-400 group-hover:text-amber-600 shrink-0" />
          </button>
        ))}
      </div>
    </div>
  );
}

export default LiveRuns;
